import React, { useState } from "react";
import { Box, Flex, IconButton, Tag, Text, useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/Toast";
import { colours } from "@/utils/themeUtils";
import { colours as themeColours } from "@/theme/colours";
import { deleteSavedFocusFromLibrary } from "@/js/api/focus";
import { ConfirmDeleteFocusModal } from "../ConfirmDeleteFocusModal";
import { useFocusStore } from "@/stores/v2/focusStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { ChakraFAIcon } from "@/components/ui/Icon";

export interface Props {
	id: string;
	name: string;
	/**
	 * Whether the focus is currently applied to any ACTIVE classes.
	 */
	active: boolean;
	/**
	 * Whether the focus is applied to a class with "Locked Focus" enabled. Independent of whether the class/focus is active.
	 */
	areTabsLocked: boolean;
	handleClickFocusItem: () => void;
	hideDeleteOption: boolean;
}

export function FocusLibraryItem(props: Props): JSX.Element {
	const { t } = useTranslation();
	const toast = useToast();
	const [isDeleting, setIsDeleting] = useState(false);
	const confirmationModal = useDisclosure();

	const focusStore = useFocusStore();

	const isLockedAndActive = props.areTabsLocked && props.active;

	function handleConfirmDelete() {
		setIsDeleting(true);
		deleteSavedFocusFromLibrary(props.id, useUserSessionStore.getState().getTeacherId() as string)
			.then(() => {
				focusStore.removeFocusItemFromLibrary(props.id);
				confirmationModal.onClose();
				toast.successToast({
					title: t("focusLibrary.focusDeletedTitle"),
					description: t("focusLibrary.focusDeletedDescription", { name: props.name }),
					isClosable: true,
				});
			})
			.catch(() => {
				toast.errorToast({
					title: t("focusLibrary.focusDeleteFailedTitle"),
					description: t("focusLibrary.focusDeleteFailedDescription", { name: props.name }),
					isClosable: true,
				});
			})
			.finally(() => {
				setIsDeleting(false);
			});
	}

	function handleClickFocusItem() {
		if (isLockedAndActive) return;
		props.handleClickFocusItem();
	}

	const isDisabled = isLockedAndActive;
	const backgroundOpacity = isDisabled ? 0.6 : 1;
	const cursor = isDisabled ? "not-allowed" : "pointer";

	return (
		<>
			<Flex
				justifyContent={"space-between"}
				alignItems={"center"}
				background={colours.blue[500]}
				mb={2}
				rounded={"md"}
				px={5}
				py={3}
				opacity={backgroundOpacity}
				data-testid={`focus-library-item-${props.id}`}
			>
				<Box as="button" flex={1} onClick={handleClickFocusItem} textAlign={"left"} cursor={cursor} disabled={isDisabled}>
					<Flex alignItems="center" gap={2}>
						<Text color={themeColours.text.title}>{props.name}</Text>
					</Flex>
				</Box>

				{props.active && (
					<Tag bg={"white"} color={"black"} fontSize={"sm"} fontWeight={400} data-testid="active-focus-label">
						{t("focusLibrary.activeLabel")}
					</Tag>
				)}

				{isLockedAndActive ? (
					<ChakraFAIcon icon={"fa-lock"} color={colours.gray[700]} boxSize={3} data-testid="locked-focus-icon" ml={3} variant="solid" />
				) : (
					!props.active &&
					!props.hideDeleteOption && (
						<IconButton
							aria-label={t("focusLibrary.deleteFocusAria")}
							variant={"unstyled"}
							boxSize={2}
							pr={0}
							pt={0}
							mt={-4}
							fontSize={"sm"}
							onClick={() => confirmationModal.onOpen()}
							data-testid="delete-focus-button"
							icon={<ChakraFAIcon icon={"fa-trash-can"} color={themeColours.text.regular} variant="solid" />}
						/>
					)
				)}
			</Flex>
			<ConfirmDeleteFocusModal
				name={props.name}
				isDeleting={isDeleting}
				isOpen={confirmationModal.isOpen}
				closeModal={confirmationModal.onClose}
				handleConfirmDelete={handleConfirmDelete}
			/>
		</>
	);
}
